import * as React from 'react'
import {maybe} from '@freckle/maybe'
import {useSafeCallbackExtraDeps, unCallbackFn} from '@freckle/react-hooks'
import isEqual from 'lodash/isEqual'
// CComponents
import {Text} from '@frontrow/console/src/components/typography'
import {Button} from '@frontrow/console/src/components/button'
import Input from '@frontrow/console/src/entities/components/base/input'

// Models
import {type DistrictT, type BasicDistrictT} from '@frontrow/console/src/models/district'
// Components
import FormField from '@frontrow/console/src/components/common/form-field'
import LabelledInput from '@frontrow/console/src/components/common/labelled-input'
import AddressForm from '@frontrow/console/src/components/common/address-form'
import {FIELD_GRID} from '@frontrow/console/src/lib/form-styles'
// Helpers
import {type AddressT} from '@frontrow/console/src/helpers/addresses'
import {extractAddress, type WeakAddressT} from '@frontrow/console/src/helpers/addresses'

// Field spans in the twelve-column grid, matching the school edit form.
const HALF = 'mb-0 pr-0 sm:col-span-6'
const FULL = 'mb-0 pr-0 sm:col-span-12'

type Props = {
  onCancel: () => void
  onSubmit: (
    newDistrictData: BasicDistrictT,
    clearClassLinkOneRosterToken: boolean,
    shouldDeleteCleverAndClasslinkAssociations?: boolean
  ) => void
  districtToUpdate?: DistrictT
  isCreate: boolean
}

type DistrictPlaceholders = {
  name: string
  dioceseId?: number
  domain?: string
  cleverDistrictId?: string
  classLinkDistrictId?: string
  ncesLeaId?: string
}

function getCreatePlaceholders(): DistrictPlaceholders {
  return {
    name: 'Freckle District',
    cleverDistrictId: '12345',
    classLinkDistrictId: 'alkl-123-s54-fds',
    ncesLeaId: ''
  }
}

function getUpdatePlaceholders(): DistrictPlaceholders {
  return {
    name: 'Name is required'
  }
}

function getDefaultDistrictState(): BasicDistrictT {
  return {
    name: '',
    city: '',
    administrativeArea: '',
    postalCode: '',
    formattedAddressLines: [],
    countryCode: '',
    dioceseId: NaN,
    domain: '',
    cleverDistrictId: '',
    classLinkDistrictId: '',
    ncesLeaId: '',
    classLinkOneRosterToken: {
      appId: '',
      bearerToken: ''
    },
    isFake: false,
    crmId: null,
    active: true
  }
}

function DistrictForm(props: Props): React.ReactElement {
  const {districtToUpdate, isCreate, onCancel, onSubmit: propsOnSubmit} = props

  const districtInState = maybe(
    () => getDefaultDistrictState(),
    dToUpdate => {
      const {
        id: _id,
        createdAt: _createdAt,
        updatedAt: _updatedAt,
        crmAccountNumber: _crmAccountNumber,
        ...restOfDistrictAttrs
      } = dToUpdate
      return restOfDistrictAttrs
    },
    districtToUpdate
  )

  const [district, setDistrict] = React.useState<BasicDistrictT>(districtInState)
  const [clearClassLinkOneRosterToken, setClearClassLinkOneRosterToken] =
    React.useState<boolean>(false)
  const [
    shouldDeleteCleverAndClasslinkAssociations,
    setShouldDeleteCleverAndClasslinkAssociations
  ] = React.useState<boolean>(false)

  const onSubmit = useSafeCallbackExtraDeps<
    (e: React.FormEvent<HTMLFormElement>) => void,
    {
      onSubmit: (
        newDistrictData: BasicDistrictT,
        clearClassLinkOneRosterToken: boolean,
        shouldDeleteCleverAndClasslinkAssociations?: boolean
      ) => void
      districtToUpdate?: DistrictT
      district: BasicDistrictT
    }
  >(
    ({onSubmit, districtToUpdate, district}) =>
      (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()

        if (districtToUpdate) {
          onSubmit(
            district,
            clearClassLinkOneRosterToken,
            shouldDeleteCleverAndClasslinkAssociations
          )
        } else {
          onSubmit(district, clearClassLinkOneRosterToken)
        }
      },
    [clearClassLinkOneRosterToken, shouldDeleteCleverAndClasslinkAssociations],
    {
      onSubmit: {value: propsOnSubmit, comparator: () => true},
      districtToUpdate: {value: districtToUpdate, comparator: isEqual},
      district: {value: district, comparator: isEqual}
    }
  )

  const onToggleClearClassLinkOneRosterToken = () => {
    setClearClassLinkOneRosterToken(prevState => !prevState)
  }

  const onToggleIsFake = () => {
    setDistrict(prevDistrict => ({...prevDistrict, isFake: !prevDistrict.isFake}))
  }

  const createFieldOnChange = (field: string, isNumber?: boolean): ((val: string) => void) => {
    return (newValue: string) => {
      const parsedValue = isNumber === true ? parseFloat(newValue) : newValue
      setDistrict(prevDistrict => ({
        ...prevDistrict,
        [field]: parsedValue
      }))
    }
  }

  const onChangeAddress = (address: AddressT) => {
    setDistrict(prevDistrict => ({...prevDistrict, ...address}))
  }

  const createClassLinkTokenFieldOnChange = (field: string): ((val: string) => void) => {
    return (newValue: string) => {
      setDistrict(prevDistrict => ({
        ...prevDistrict,
        classLinkOneRosterToken: {
          appId: prevDistrict.classLinkOneRosterToken?.appId || '',
          bearerToken: prevDistrict.classLinkOneRosterToken?.bearerToken || '',
          [field]: newValue
        }
      }))
    }
  }

  const onToggleDeleteCleverAndClasslinkAssociations = () => {
    setShouldDeleteCleverAndClasslinkAssociations(prevState => !prevState)
  }

  const {name, isFake, cleverDistrictId, classLinkDistrictId, classLinkOneRosterToken} = district

  const {classLinkOneAppId, classLinkOneBearerToken} = maybe(
    () => ({
      classLinkOneAppId: '',
      classLinkOneBearerToken: ''
    }),
    token => {
      return {
        classLinkOneAppId: token.appId,
        classLinkOneBearerToken: token.bearerToken
      }
    },
    classLinkOneRosterToken
  )

  const crmManaged = districtToUpdate ? districtToUpdate.active && !!districtToUpdate.crmId : false

  const placeholders = districtToUpdate ? getUpdatePlaceholders() : getCreatePlaceholders()

  const submitButtonText = districtToUpdate ? 'Update District' : 'Create District'

  const deleteCleverClasslinkCheckbox = districtToUpdate ? (
    <div className={FULL}>
      <LabelledInput label="Wipe Clever/Classlink IDs">
        <input
          type="checkbox"
          checked={shouldDeleteCleverAndClasslinkAssociations}
          onChange={onToggleDeleteCleverAndClasslinkAssociations}
        />
      </LabelledInput>
    </div>
  ) : null

  const updateFields = isCreate ? null : (
    <React.Fragment>
      <hr className="border-border sm:col-span-12" />

      <FormField
        wrapperStyle={HALF}
        label="ClassLink Tenant Id"
        htmlFor="district-class-link-tenant-id"
      >
        <Input
          id="district-class-link-tenant-id"
          className="font-mono"
          value={classLinkDistrictId || ''}
          type="text"
          placeholder={placeholders.classLinkDistrictId}
          onChange={createFieldOnChange('classLinkDistrictId')}
        />
      </FormField>
      <FormField wrapperStyle={HALF} label="Clever Id" htmlFor="district-clever-id">
        <Input
          id="district-clever-id"
          className="font-mono"
          value={cleverDistrictId || ''}
          type="text"
          placeholder={placeholders.cleverDistrictId}
          onChange={createFieldOnChange('cleverDistrictId')}
        />
      </FormField>

      <FormField
        wrapperStyle={HALF}
        label="ClassLink Application Id"
        htmlFor="district-class-link-app-id"
      >
        <Input
          id="district-class-link-app-id"
          className="font-mono"
          value={classLinkOneAppId || ''}
          type="text"
          placeholder={placeholders.classLinkDistrictId}
          onChange={createClassLinkTokenFieldOnChange('appId')}
        />
      </FormField>
      <FormField
        wrapperStyle={HALF}
        label="ClassLink Access Token"
        htmlFor="district-class-link-token"
      >
        <Input
          id="district-class-link-token"
          className="font-mono"
          value={classLinkOneBearerToken || ''}
          type="text"
          placeholder={placeholders.cleverDistrictId}
          onChange={createClassLinkTokenFieldOnChange('bearerToken')}
        />
      </FormField>

      <div className={FULL}>
        <LabelledInput label="Clear any existing ClassLink One token">
          <input
            type="checkbox"
            checked={clearClassLinkOneRosterToken}
            onChange={onToggleClearClassLinkOneRosterToken}
          />
        </LabelledInput>
      </div>

      {deleteCleverClasslinkCheckbox}

      <Text style="body-2" addClass="text-xs italic text-muted-text sm:col-span-12">
        <strong>Note</strong>: For security reasons, current ClassLink One Roster auth data is not
        shown
      </Text>
    </React.Fragment>
  )

  return (
    <form onSubmit={unCallbackFn(onSubmit)} action="">
      <div className={FIELD_GRID}>
        <Text style="body-2" addClass="text-xs text-muted-text sm:col-span-12">
          * Required
        </Text>

        <FormField wrapperStyle={FULL} label="District Name" htmlFor="district-name" required>
          <Input
            id="district-name"
            value={name}
            type="text"
            required
            placeholder={placeholders.name}
            onChange={createFieldOnChange('name')}
            disabled={crmManaged}
            dataTest="district-form-field"
          />
        </FormField>

        <div className={FULL}>
          <LabelledInput label="Is this a fake district?">
            <input type="checkbox" checked={isFake} onChange={onToggleIsFake} />
          </LabelledInput>
          <p className="mt-1 text-xs italic text-muted-text">
            Fake districts can be associated with schools that don&apos;t have a real one.
          </p>
        </div>

        <div className={FULL}>
          <AddressForm
            isNewAddress={districtToUpdate === null || districtToUpdate === undefined}
            address={extractAddress(district as WeakAddressT)}
            onChangeAddress={onChangeAddress}
            disabled={crmManaged}
          />
        </div>

        {updateFields}
      </div>

      <div className="mt-6 flex justify-end gap-3">
        <Button style="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit">{submitButtonText}</Button>
      </div>
    </form>
  )
}

export default DistrictForm
